import {
  getPortfolioFromLocalStorage,
  getPortfolioUpdatedAtFromLocalStorage,
  markPortfolioDbSyncedInLocalStorage,
  setPortfolioToLocalStorage,
} from '@/localStorage'
import type { Portfolio } from '@/types'
import { getDatabaseSnapshot } from '@/utils/database'
import {
  type DbSyncTelemetry,
  measureDbSyncTelemetryPhase,
} from './dbSyncTelemetry'
import { uploadPortfolioDbFileToRemote } from './remoteDbSync'

/**
 * dirty な portfolio DB の一貫した file snapshot を remote へ upload する。
 * @param params portfolio と認証 token 取得関数を受け取る。
 */
export const syncDbFileToRemote = async (params: {
  readonly portfolio: Portfolio
  readonly getToken: () => Promise<string | null>
  readonly telemetry?: DbSyncTelemetry
}): Promise<void> => {
  const { portfolio, getToken, telemetry } = params

  const localUpdatedAt = getPortfolioUpdatedAtFromLocalStorage() ?? 0
  if (localUpdatedAt <= portfolio.updatedAt) {
    return
  }

  const snapshot = await measureDbSyncTelemetryPhase({
    telemetry,
    phase: 'db-backup.snapshot',
    task: () => getDatabaseSnapshot({ id: portfolio.uuid }),
  })
  telemetry?.setRawDbSizeBytes({ bytes: snapshot.file.size })
  if (snapshot.updatedAt <= portfolio.updatedAt) {
    return
  }

  await uploadPortfolioDbFileToRemote({
    portfolioId: portfolio.uuid,
    timestamp: snapshot.updatedAt,
    getToken,
    file: snapshot.file,
    telemetry,
  })

  const currentPortfolio = getPortfolioFromLocalStorage()
  if (currentPortfolio?.uuid !== portfolio.uuid) {
    return
  }

  setPortfolioToLocalStorage({
    ...portfolio,
    updatedAt: snapshot.updatedAt,
  })
  markPortfolioDbSyncedInLocalStorage({
    portfolioId: portfolio.uuid,
    syncedUpdatedAt: snapshot.updatedAt,
  })
}
